import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { TaskService } from '../services/taskService';
import { UserCredentials, AuthContextType, UserType, AuthResponse } from '../types/auth';
import { SessionService } from '../services/sessionService';
import SessionWarning from '../components/SessionWarning';
import { getSessionConfig } from '../config/sessionConfig';

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningTime, setWarningTime] = useState(0);
  
  const taskServiceRef = useRef(new TaskService(authService));
  
  // Use useRef to maintain the session service instance across renders
  const sessionServiceRef = useRef<SessionService | null>(null);

  // Initialize SessionService when authenticated
  useEffect(() => {
    if (isAuthenticated && !sessionServiceRef.current) {
      // Get session config from server response (stored in localStorage) or use defaults
      const config = getSessionConfig();
      
      // Override with server-provided values if available
      if (localStorage.getItem('inactivityTimeout')) {
        config.inactivityTimeout = parseInt(localStorage.getItem('inactivityTimeout') || String(config.inactivityTimeout));
      }
      
      if (localStorage.getItem('warningPeriod')) {
        config.warningPeriod = parseInt(localStorage.getItem('warningPeriod') || String(config.warningPeriod));
      }
      
      if (localStorage.getItem('maxSessionDuration')) {
        config.maxSessionDuration = parseInt(localStorage.getItem('maxSessionDuration') || String(config.maxSessionDuration));
      }
      
      // Create new SessionService instance
      sessionServiceRef.current = new SessionService(authService, config);
      
      // Set up session warning callback
      sessionServiceRef.current.setSessionWarningCallback((remainingTime) => {
        setWarningTime(remainingTime);
        setShowSessionWarning(true);
      });
      
      // Set up session expiration callback
      sessionServiceRef.current.setSessionExpiredCallback(() => {
        logout();
      });
    } else if (!isAuthenticated && sessionServiceRef.current) {
      // Clean up session service when logged out
      sessionServiceRef.current.cleanup();
      sessionServiceRef.current = null;
      setShowSessionWarning(false);
    }
    
    // Clean up on unmount
    return () => {
      if (sessionServiceRef.current) {
        sessionServiceRef.current.cleanup();
      }
    };
  }, [isAuthenticated]);
  
  // Helper function to determine if an error is authentication-related
  const isAuthError = (error: any): boolean => {
    // Check for Axios error structure
    if (error?.response?.status) {
      return error.response.status === 401 || error.response.status === 403;
    }
    
    // Check for error message patterns that indicate auth issues
    if (error?.message) {
      const authErrorPatterns = [
        /unauthorized/i,
        /forbidden/i,
        /authentication failed/i,
        /invalid token/i,
        /expired token/i
      ];
      
      return authErrorPatterns.some(pattern => pattern.test(error.message));
    }
    
    return false;
  };
  
  // Initial auth check
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const refreshToken = localStorage.getItem('refreshToken');
        const savedUserType = localStorage.getItem('userType') as UserType | null;

        if (token && savedUserType) {
          try {
            // Validate token on the server
            const isValid = await authService.validateToken(token);
            
            if (isValid) {
              authService.setToken(token);
              setIsAuthenticated(true);
              setUserType(savedUserType);
            } else if (refreshToken) {
              // Try to refresh the token if validation fails
              try {
                const response = await authService.refreshToken(refreshToken);
                if (response && response.token) {
                  authService.setToken(response.token);
                  setIsAuthenticated(true);
                  setUserType(response.user_type as UserType);
                } else {
                  clearAuthState();
                }
              } catch (refreshError: any) {
                // Only clear auth state for auth-related errors, not for server or network errors
                if (isAuthError(refreshError)) {
                  console.error('Token refresh failed with auth error:', refreshError);
                  clearAuthState();
                } else {
                  console.error('Token refresh failed with non-auth error:', refreshError);
                  // Keep existing token
                  authService.setToken(token);
                  setIsAuthenticated(true);
                  setUserType(savedUserType);
                }
              }
            } else {
              clearAuthState();
            }
          } catch (validationError: any) {
            // Only clear auth state for auth-related errors, not for server or network errors
            if (isAuthError(validationError)) {
              console.error('Token validation failed with auth error:', validationError);
              clearAuthState();
            } else {
              console.error('Token validation failed with non-auth error:', validationError);
              // Keep existing token
              authService.setToken(token);
              setIsAuthenticated(true);
              setUserType(savedUserType);
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error in auth initialization:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    const clearAuthState = () => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userType');
      localStorage.removeItem('userId');
      setIsAuthenticated(false);
      setUserType(null);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: UserCredentials): Promise<AuthResponse> => {
    try {
      const response = await authService.login(credentials);
      if (response && response.token) {
        // Store auth tokens
        authService.setToken(response.token);
        localStorage.setItem('authToken', response.token);
        
        // Store refresh token if available
        if (response.refresh_token) {
          localStorage.setItem('refreshToken', response.refresh_token);
        }
        
        // Store user info
        localStorage.setItem('userType', response.user_type);
        localStorage.setItem('userId', response.user_id);
        
        // Store session config if provided
        if (response.inactivity_timeout) {
          localStorage.setItem('inactivityTimeout', (response.inactivity_timeout as number * 1000).toString());
        }
        if (response.warning_period) {
          localStorage.setItem('warningPeriod', (response.warning_period as number * 1000).toString());
        }
        if (response.max_session_duration) {
          localStorage.setItem('maxSessionDuration', (response.max_session_duration as number * 1000).toString());
        }
        
        setIsAuthenticated(true);
        setUserType(response.user_type as UserType);
        return response;
      } else {
        throw new Error('Login failed: Invalid response');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear session service
    if (sessionServiceRef.current) {
      sessionServiceRef.current.cleanup();
      sessionServiceRef.current = null;
    }
    
    // Clear auth state
    authService.logout();
    setIsAuthenticated(false);
    setUserType(null);
    setShowSessionWarning(false);
  };
  
  // Handle session continuation when user clicks "Continue Session"
  const handleContinueSession = () => {
    setShowSessionWarning(false);
    if (sessionServiceRef.current) {
      sessionServiceRef.current.continueSession();
    }
  };
  
  // Handle session expiration
  const handleSessionExpired = () => {
    setShowSessionWarning(false);
    logout();
  };

  const register = async (userData: UserCredentials & { full_name: string; user_type: UserType; invite_code?: string }) => {
    try {
      await authService.register(userData);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    userType,
    isLoading,
    login,
    logout,
    register,
    taskService: taskServiceRef.current,
    refreshSession: () => {
      if (sessionServiceRef.current) {
        return sessionServiceRef.current.refreshToken();
      }
      return Promise.resolve(false);
    },
    // Debug method to manually trigger the session warning
    debugTriggerSessionWarning: () => {
      setWarningTime(10000); // 10 seconds warning
      setShowSessionWarning(true);
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showSessionWarning && (
        <SessionWarning
          remainingTime={warningTime}
          onContinue={handleContinueSession}
          onExpire={handleSessionExpired}
        />
      )}
    </AuthContext.Provider>
  );
};